import * as React from 'react';
import { useCallback, useRef } from 'react';
import classNames from 'classnames';
import { IconMinus12, IconPlus16, Spinner, UIText2 } from '@cian/ui-kit';

import * as styles from './DeprecatedBetInput.css';
import { filterNegativeNumber } from './utils/filterNegativeNumber';
import { parseInputToNumber } from './utils/parseInputToNumber';
import { setFocusToInputEnd } from './utils/setFocusToInputEnd';
import { numberToPrettyString } from '@cian/utils';

export interface IBetInputProps {
  value: number;
  loading: boolean;
  previousDisabled: boolean;
  nextDisabled: boolean;
  suffix?: string;
  disabled: boolean;

  onChange(value: number): void;

  onPreviousClick(): void;

  onNextClick(): void;
}

export const DeprecatedBetInput = (props: IBetInputProps) => {
  const { value, loading, disabled, previousDisabled, nextDisabled, suffix, onChange, onPreviousClick, onNextClick } =
    props;

  const handleChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const fallback = 0;

      let value = parseInputToNumber(event.target.value, fallback);
      value = filterNegativeNumber(value, fallback);

      onChange(value);
    },
    [onChange],
  );

  const ref = useRef<HTMLInputElement>(null);

  const handleLabelClick = useCallback(() => {
    if (ref.current) {
      setFocusToInputEnd(ref.current);
    }
  }, []);

  return (
    <div className={classNames(styles['wrapper'], loading && styles['loading'])}>
      <button
        type="button"
        data-testid="BetMinusButton"
        disabled={previousDisabled || loading || disabled}
        onClick={onPreviousClick}
        className={classNames(styles['button'], styles['previous'])}
      >
        <IconMinus12 color="current_color" />
      </button>

      <input
        ref={ref}
        type="text"
        data-testid="BetInput"
        id="BetInput"
        pattern="[0-9]*"
        inputMode="numeric"
        maxLength={6}
        disabled={loading || disabled}
        value={numberToPrettyString(value)}
        placeholder="Введите ставку"
        className={classNames(styles['input'], Boolean(suffix) && styles['input_with-suffix'])}
        onChange={handleChange}
      />

      {suffix && !loading && (
        <label onClick={handleLabelClick} htmlFor="BetInput" data-testid="BetInputLabel" className={styles['suffix']}>
          <UIText2 color="black_100">{suffix}</UIText2>
        </label>
      )}

      {loading && (
        <div className={styles['spinner_wrapper']} data-testid="spinner">
          <Spinner size={16} />
        </div>
      )}

      <button
        type="button"
        data-testid="BetPlusButton"
        disabled={nextDisabled || loading || disabled}
        onClick={onNextClick}
        className={classNames(styles['button'], styles['next'])}
      >
        <IconPlus16 color="current_color" />
      </button>
    </div>
  );
};
